import { api } from '../api/http';
import { needsIosInstall, pushSupported } from './platform';

export type PushPermissionState = 'unsupported' | 'ios-install' | 'default' | 'granted' | 'denied';

export function pushPermission(): PushPermissionState {
  if (needsIosInstall()) return 'ios-install';
  if (!pushSupported()) return 'unsupported';
  return Notification.permission;
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.ready;
}

/** The browser's current push subscription for this device, if any. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

/**
 * Asks for permission (only ever from our own "Turn on" buttons, DESIGN 4.18.5), subscribes with
 * the server's VAPID key, registers this device and sends a silent test.
 * Resolves to the resulting permission.
 */
export async function enablePush(): Promise<NotificationPermission> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission;
  const { publicKey } = await api('vapidPublicKey');
  const reg = await registration();
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));
  const json = sub.toJSON();
  await api('pushSubscribe', {
    body: {
      endpoint: sub.endpoint,
      expirationTime: sub.expirationTime,
      keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
    },
  });
  void api('pushTest').catch(() => undefined);
  return permission;
}

/** Turns push off for this device only. */
export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  const endpoint = sub?.endpoint;
  await sub?.unsubscribe();
  await api('pushUnsubscribe', { body: endpoint ? { endpoint } : {} });
}

/** Mirrors the unread count to the app icon where supported. */
export function setAppBadge(count: number): void {
  if (!('setAppBadge' in navigator)) return;
  const p = count > 0 ? navigator.setAppBadge(count) : navigator.clearAppBadge();
  p.catch(() => undefined);
}
